/**
 * Every country, as its ISO 3166-1 alpha-2 code — the profile's country, and
 * Rideboard's from / to (plan.md step 68). Codes only: a country's NAME comes
 * from the runtime in the reader's language (`Intl.DisplayNames`), so there
 * is no list of names to keep in step with anything.
 */
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

const KNOWN = new Set<string>(COUNTRY_CODES);

/** Whether `code` is a country (upper-case alpha-2). */
export function isCountryCode(code: unknown): code is CountryCode {
  return typeof code === 'string' && KNOWN.has(code);
}

/**
 * Europe — the countries GeoNames files under the continent EU (its
 * `countryInfo.txt`; Kosovo's XK is not an ISO code and is left out). Where a
 * ride may come from when the rave is in one of them (the user, 2026-10-15).
 */
export const EUROPE_COUNTRIES = [
  'AD', 'AL', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FO', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT', 'JE', 'LI', 'LT', 'LU',
  'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SJ',
  'SK', 'SM', 'UA', 'VA',
] as const satisfies readonly CountryCode[];

const EUROPE = new Set<string>(EUROPE_COUNTRIES);

/**
 * The countries a ride to or from a rave may start or end in (Rideboard,
 * plan.md step 68 — the user, 2026-10-15): a rave in a EUROPEAN country takes
 * the whole continent; anywhere else (Israel, the USA, Brazil…) only its own
 * country; the rave admin may WIDEN either with `extra`. A rave whose country
 * is not known yet locks nobody out — every country. Sorted, each once; what
 * is not a country is dropped. The API enforces it on posts, the screens'
 * pickers offer exactly this list.
 */
export function rideCountriesFor(raveCountry: string | null | undefined, extra: readonly string[] = []): string[] {
  const home = (raveCountry ?? '').toUpperCase();
  if (!isCountryCode(home)) return [...COUNTRY_CODES];
  const allowed = new Set<string>(EUROPE.has(home) ? EUROPE_COUNTRIES : [home]);
  for (const c of extra) {
    const code = (c ?? '').toUpperCase();
    if (isCountryCode(code)) allowed.add(code);
  }
  return [...allowed].sort();
}

/**
 * The continent a country is in, for Find a rave's rows in the phone app (the user,
 * 2026-10-26: "Europe, America, etc." — by continent, the Middle East its own). Europe is
 * GeoNames' EU list above; Central America and the Caribbean count as North America; Turkey
 * and the Caucasus as the Middle East; Russia stays European as in GeoNames. Anything not
 * known is `null` (no row).
 */
export type Continent = 'europe' | 'north-america' | 'south-america' | 'middle-east' | 'asia' | 'africa' | 'oceania';

const CONTINENT_LISTS: Record<Exclude<Continent, 'europe'>, readonly string[]> = {
  'north-america': ['US', 'CA', 'MX', 'GL', 'BM', 'PM', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA', 'CU', 'JM', 'HT', 'DO', 'PR',
    'BS', 'BB', 'TT', 'AG', 'DM', 'GD', 'KN', 'LC', 'VC', 'AW', 'CW', 'SX', 'BQ', 'KY', 'TC', 'VG', 'VI', 'AI', 'MS', 'GP', 'MQ', 'BL', 'MF'],
  'south-america': ['AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE', 'GF', 'FK'],
  'middle-east': ['IL', 'PS', 'JO', 'LB', 'SY', 'IQ', 'IR', 'SA', 'AE', 'QA', 'BH', 'KW', 'OM', 'YE', 'TR', 'EG', 'GE', 'AM', 'AZ'],
  asia: ['CN', 'JP', 'KR', 'KP', 'TW', 'HK', 'MO', 'MN', 'IN', 'PK', 'BD', 'LK', 'NP', 'BT', 'MV', 'AF', 'KZ', 'KG', 'TJ', 'TM', 'UZ',
    'TH', 'VN', 'LA', 'KH', 'MM', 'MY', 'SG', 'ID', 'PH', 'BN', 'TL'],
  africa: ['DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CV', 'CM', 'CF', 'TD', 'KM', 'CG', 'CD', 'CI', 'DJ', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM',
    'GH', 'GN', 'GW', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'YT', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RE', 'RW', 'SH',
    'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'EH', 'ZM', 'ZW'],
  oceania: ['AU', 'NZ', 'FJ', 'PG', 'SB', 'VU', 'NC', 'PF', 'WS', 'TO', 'KI', 'FM', 'MH', 'PW', 'NR', 'TV', 'CK', 'NU', 'GU', 'MP', 'AS'],
};
const CONTINENT_OF = new Map<string, Continent>([
  ...EUROPE_COUNTRIES.map((c) => [c, 'europe'] as [string, Continent]),
  ...Object.entries(CONTINENT_LISTS).flatMap(([k, list]) => list.map((c) => [c, k as Continent] as [string, Continent])),
]);

/** The continent of an ISO alpha-2 country, or null when it is not known. */
export function continentOf(country: string | null | undefined): Continent | null {
  return CONTINENT_OF.get((country ?? '').toUpperCase()) ?? null;
}

/** Letters NFD leaves whole — folded by hand. */
const FOLD: Record<string, string> = { ł: 'l', ø: 'o', ß: 'ss', đ: 'd', æ: 'ae', œ: 'oe', ı: 'i', þ: 'th', ð: 'd' };

/**
 * A city name as a search key: lower case, no accents, single spaces —
 * "Győr" and "gyor" are one key. The cities importer stores it and the API's
 * city search folds what is typed with it, so the two can never disagree.
 */
export function cityKey(name: string): string {
  return name
    .normalize('NFD').replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[łøßđæœıþð]/g, (c) => FOLD[c] ?? c)
    .replace(/\s+/g, ' ')
    .trim();
}
